import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { fetchAllMasterData } from "@/store/slices/masterSlice";
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useLocation } from "react-router-dom";
import {
  fetchAirLoomData,
  resetAirJetLoomSliceState,
  setCurrentTab,
} from "../../store/slices/airJetLoomSlice";
import PersonalDetails from "./NewLoomForms/PersonalDetails";
import LoomDetails from "./NewLoomForms/LoomDetails";
import BankDetails from "./NewLoomForms/BankDetails";
import PriorityDetails from "./NewLoomForms/PriorityDetails";
import DeclarationDetails from "./NewLoomForms/DeclarationDetails";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import UploadRequirementsDialog from "../../components/common/UploadRequirementsDialog";
import { Button } from "../../components/ui/button";
import NewPurchasedDetails from "./NewLoomForms/NewPurchasedDetails";

const AirJetLooms = () => {
  const { current_tab, data, data_loading, user_initiated_nav } = useSelector(
    (state) => state.airJetLoom
  );

  const {
    districts = [],
    communities = [],
    loading,
  } = useSelector((state) => state.master || {});
  const dispatch = useDispatch();
  const location = useLocation();
  const [completedTabs, setCompletedTabs] = useState(new Set());
  const [declarationCompleted, setDeclarationCompleted] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    setOpenModal(true);
  }, []);

  const tabsOrder = [
    "personal",
    "eligibility",
    "loom",
    "modernization",
    "scheme",
    "bank",
    "priority",
    "purchase_status",
    ...(data?.already_purchase === "No" ? ["provisional_sanction"] : []),
    ...(data?.already_purchase == "Yes" ||
    (data?.already_purchase == "No" && data?.status == "recommend")
      ? ["purchase_detail"]
      : []),
    ...(data?.already_purchase == "Yes" ||
    (data?.already_purchase == "No" && data?.status == "recommend")
      ? ["declaration"]
      : []),
  ];

  useEffect(() => {
    dispatch(resetAirJetLoomSliceState());
    dispatch(fetchAllMasterData());
    dispatch(fetchAirLoomData());
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      const searchParams = new URLSearchParams(location.search);
      const tabParam = searchParams.get("tab");
      const newCompletedTabs = new Set();
      let nextTab = "personal";

      if (data?.id) {
        newCompletedTabs.add("personal");
        nextTab = "eligibility";
      }

      if (data?.id && data?.eligibility_status == "Eligible") {
        newCompletedTabs.add("eligibility");
        nextTab = "loom";
      }

      if (
        data?.id &&
        data?.eligibility_status == "Eligible" &&
        data?.premises_type
      ) {
        newCompletedTabs.add("loom");
        nextTab = "modernization";
      }

      if (
        data.id &&
        data?.eligibility_status == "Eligible" &&
        data?.premises_type &&
        data?.willingness_of_modernization
      ) {
        newCompletedTabs.add("modernization");
        nextTab = "scheme";
      }

      if (
        data.id &&
        data?.eligibility_status == "Eligible" &&
        data?.premises_type &&
        data?.willingness_of_modernization &&
        data?.other_scheme_benefited
      ) {
        newCompletedTabs.add("scheme");
        nextTab = "bank";
      }

      if (
        data.id &&
        data?.eligibility_status == "Eligible" &&
        data?.premises_type &&
        data?.willingness_of_modernization &&
        data?.bank_name &&
        data?.other_scheme_benefited
      ) {
        newCompletedTabs.add("bank");
        nextTab = "priority";
      }

      if (
        data.id &&
        data?.eligibility_status == "Eligible" &&
        data?.premises_type &&
        data?.willingness_of_modernization &&
        data?.bank_name &&
        data?.other_scheme_benefited &&
        data?.export_oriented
      ) {
        newCompletedTabs.add("priority");
        nextTab = "purchase_status";
      }

      if (
        data.id &&
        data?.eligibility_status == "Eligible" &&
        data?.premises_type &&
        data?.willingness_of_modernization &&
        data?.bank_name &&
        data?.other_scheme_benefited &&
        data?.date_of_purchase
      ) {
        newCompletedTabs.add("purchase_status");
        if (data?.already_purchase == "Yes") {
          nextTab = "purchase_detail";
        } else {
          nextTab = "provisional_sanction";
        }
      }

      if (
        data.id &&
        data?.eligibility_status == "Eligible" &&
        data?.premises_type &&
        data?.willingness_of_modernization &&
        data?.bank_name &&
        data?.other_scheme_benefited &&
        data?.date_of_purchase &&
        data?.proposed_no_of_loom &&
        (data?.status == "waiting for recommendation" ||
          data?.status == "recommend" ||
          data?.status == "not recommend")
      ) {
        newCompletedTabs.add("provisional_sanction");
        if (data?.status == "recommend") nextTab = "purchase_detail";
      }

      if (
        data.id &&
        data?.eligibility_status == "Eligible" &&
        data?.premises_type &&
        data?.willingness_of_modernization &&
        data?.bank_name &&
        data?.other_scheme_benefited &&
        data?.date_of_purchase &&
        data?.total_looms_upgraded
      ) {
        newCompletedTabs.add("purchase_detail");
        nextTab = "declaration";
      }

      setCompletedTabs(newCompletedTabs);
      if (tabParam && tabsOrder.includes(tabParam)) {
        dispatch(setCurrentTab(tabParam));
      } else if (!user_initiated_nav) {
        dispatch(setCurrentTab(nextTab));
      }
    }
  }, [data, dispatch, location.search, user_initiated_nav]);

  const getCompletionPercentage = () => {
    const completedCount = completedTabs.size + (declarationCompleted ? 1 : 0);
    const totalTabs = tabsOrder.length;
    return Math.round((completedCount / totalTabs) * 100);
  };

  const isTabDisabled = (tabId) => {
    const tabIndex = tabsOrder.indexOf(tabId);
    if (tabIndex === 0) return false;

    for (let i = 0; i < tabIndex; i++) {
      if (!completedTabs.has(tabsOrder[i])) {
        return true;
      }
    }

    return false;
  };

  const handleTabComplete = (tabId) => {
    if (data?.eligibility_status != "Eligible") {
      setCurrentTab("eligibility");
    } else if (tabId === "declaration") {
      setDeclarationCompleted(true);
      setCompletedTabs((prev) => new Set([...prev, "declaration"]));
    } else {
      setCompletedTabs((prev) => new Set([...prev, tabId]));
    }
  };

  const showCheckmark = (tabId) => {
    if (tabId === "declaration") {
      return declarationCompleted;
    }
    return completedTabs.has(tabId);
  };

  const uploads_array = [
    { name: "Profile Photo", type: "JPG, JPEG, PNG max size 2MB" },
    { name: "Aadhaar Copy", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    {
      name: "GPS Location",
      type: "PNG, JPG, JPEG max size 2MB",
      notes:
        "Install the application https://play.google.com/store/apps/details?id=com.gpsmapcamera.geotagginglocationonphoto and capture the photo to upload",
    },
    {
      name: "Electricity Bill",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Udyog Aadhaar",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "SSI Registration",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    { name: "Invoice Copy", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    { name: "Bank Passbook", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    {
      name: "Scheme Documents",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    { name: "Applicant Signature", type: "PNG, JPG, JPEG max size 2MB" },
    {
      name: "Export Oriented Copy",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Cluster Approach Copy",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Opt Out Declaration Copy",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-primary">
          Purchase of brand new Air Jet Looms
        </h1>
        {data?.id && (
          <p className="text-muted-foreground">
            Application Number : {data?.application_no || "N/A"}
          </p>
        )}
        {data?.id &&
          data?.beneficiary_type &&
          data?.eligibility_status != "Eligible" && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Sorry, You are not Eligible to proceed further in this form.
              </AlertDescription>
            </Alert>
          )}
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpenModal(true)}
        >
          Application Requirements
        </Button>
      </div>
      {loading || data_loading ? (
        <div className="flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Application Progress</span>
              <span>{getCompletionPercentage()}% Complete</span>
            </div>
            <Progress value={getCompletionPercentage()} className="h-2" />
          </div>

          <Tabs
            value={current_tab}
            onValueChange={(value) => dispatch(setCurrentTab(value))}
            className="space-y-6"
          >
            <TabsList className="grid w-full grid-cols-8">
              <TabsTrigger
                value="personal"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("personal")}
              >
                Personal
                <br /> Details
                {showCheckmark("personal") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="eligibility"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("eligibility")}
              >
                Eligibility
                <br /> Criteria
                {showCheckmark("eligibility") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="loom"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("loom")}
              >
                Loom
                <br /> Details
                {showCheckmark("loom") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="modernization"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("modernization")}
              >
                Modernization
                <br /> Details
                {showCheckmark("modernization") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="scheme"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("scheme")}
              >
                Scheme & <br />
                Product details
                {showCheckmark("scheme") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="bank"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("bank")}
              >
                Bank
                <br /> Details
                {showCheckmark("bank") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="priority"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("priority")}
              >
                Priority
                <br /> Details
                {showCheckmark("priority") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="purchase_status"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("purchase_status")}
              >
                Purchase
                <br /> Status
                {showCheckmark("purchase_status") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              {/* {data?.already_purchase == "No" && (
                <> */}
              <TabsTrigger
                value="provisional_sanction"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("provisional_sanction")}
              >
                Provisional <br /> Sanction
                {showCheckmark("provisional_sanction") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              {/* </>
              )} */}
              {/* {(data?.already_purchase == "Yes" ||
                (data?.already_purchase == "No" &&
                  data?.status == "recommend")) && (
                <> */}
              <TabsTrigger
                value="purchase_detail"
                className="flex items-center gap-1"
                // disabled={isTabDisabled("purchase_detail")}
              >
                Purchase <br /> Details
                {showCheckmark("purchase_detail") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="declaration"
                className="flex items-center gap-1"
                disabled={true}
                // disabled={isTabDisabled("declaration")}
              >
                Declaration
                {showCheckmark("declaration") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              {/* </>
              )} */}
            </TabsList>

            <TabsContent value="personal">
              <PersonalDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("personal")}
              />
            </TabsContent>
            <TabsContent value="loom">
              <LoomDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("loom")}
              />
            </TabsContent>
            <TabsContent value="bank">
              <BankDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("bank")}
              />
            </TabsContent>
            <TabsContent value="priority">
              <PriorityDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("priority")}
              />
            </TabsContent>
            <TabsContent value="purchase_detail">
              <NewPurchasedDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("purchase_detail")}
              />
            </TabsContent>
            <TabsContent value="declaration">
              <DeclarationDetails
                districts={districts}
                communities={communities}
                data={data}
                onTabComplete={() => handleTabComplete("declaration")}
              />
            </TabsContent>
          </Tabs>
          <UploadRequirementsDialog
            openModal={openModal}
            setOpenModal={setOpenModal}
            uploads_array={uploads_array}
          />
        </>
      )}
    </div>
  );
};

export default AirJetLooms;
